import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { AuthContext } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import Pagination from '../components/Pagination';
import { usePageSize } from '../hooks/usePagination';
import './SubscriptionHistory.css';

// A subscription always belongs to a Hotel or a Spa, so it can link straight
// to that listing's detail page.
const ENTITY_LINKS = {
  Hotel: { path: 'hotels', permission: 'hotels.view', label: 'hotel' },
  Spa: { path: 'spas', permission: 'spas.view', label: 'spa' },
};

const EMPTY_FILTERS = { entityType: '', subscriptionType: '', status: '', expiring: '', page: 1 };
const EXPIRING_OPTIONS = ['7', '15', '30'];

const filtersFromParams = (params) => {
  const expiring = EXPIRING_OPTIONS.includes(params.get('expiring')) ? params.get('expiring') : '';
  const page = parseInt(params.get('page'), 10);
  return {
    entityType: params.get('entityType') || '',
    subscriptionType: params.get('subscriptionType') || '',
    // Expiring-soon is always an "active" view
    status: expiring ? 'active' : params.get('status') || '',
    expiring,
    page: Number.isInteger(page) && page > 0 ? page : 1,
  };
};

const paramsFromFilters = (filters) => {
  const params = {};
  if (filters.entityType) params.entityType = filters.entityType;
  if (filters.subscriptionType) params.subscriptionType = filters.subscriptionType;
  if (filters.expiring) params.expiring = filters.expiring;
  else if (filters.status) params.status = filters.status;
  if (filters.page > 1) params.page = String(filters.page);
  return params;
};

const sameFilters = (a, b) => Object.keys(EMPTY_FILTERS).every((key) => a[key] === b[key]);

const SubscriptionHistory = () => {
  const navigate = useNavigate();
  const { hasPermission } = useContext(AuthContext);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Filters live in the URL so the dashboard can deep-link straight into a
  // filtered view (e.g. ?expiring=7) and the back button restores them.
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => filtersFromParams(searchParams));
  useEffect(() => {
    const next = filtersFromParams(searchParams);
    setFilters((current) => (sameFilters(current, next) ? current : next));
  }, [searchParams]);
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 1,
    currentPage: 1
  });
  const [pageSize, setPageSize] = usePageSize();

  // Detail modal. The single-record endpoint also resolves `activatedBy`,
  // which the list response does not carry.
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pageSize]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.entityType) params.append('entityType', filters.entityType);
      if (filters.subscriptionType) params.append('subscriptionType', filters.subscriptionType);
      if (filters.status) params.append('status', filters.status);
      if (filters.expiring) params.append('expiringWithinDays', filters.expiring);
      params.append('page', filters.page);
      params.append('limit', String(pageSize));

      const response = await axios.get(`${API_BASE_URL}/admin/subscription-history?${params}`);
      setHistory(response.data.data || []);
      setPagination({
        total: response.data.total || 0,
        pages: response.data.pages || 1,
        currentPage: response.data.page || 1
      });
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch subscription history');
      setLoading(false);
    }
  };

  const applyFilters = (next) => {
    setFilters(next);
    setSearchParams(paramsFromFilters(next), { replace: true });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    // "Expiring soon" only makes sense for active subscriptions
    const next = { ...filters, [name]: value, page: 1 };
    if (name === 'expiring' && value) next.status = 'active';
    if (name === 'status' && value && value !== 'active') next.expiring = '';
    applyFilters(next);
  };

  const clearFilters = () => applyFilters({ ...EMPTY_FILTERS });

  const hasFilters = Boolean(filters.entityType || filters.subscriptionType || filters.status || filters.expiring);

  const handlePageChange = (newPage) => {
    applyFilters({ ...filters, page: newPage });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openDetails = async (entry) => {
    // Show what the list already has straight away, then fill in the rest
    setSelected(entry);
    setDetailError('');
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/subscription-history/${entry._id}`);
      if (response.data?.success) {
        setSelected(response.data.data);
      } else {
        setDetailError(response.data?.message || 'Failed to load full record');
      }
    } catch (err) {
      setDetailError(err?.response?.data?.message || err.message || 'Failed to load full record');
    } finally {
      setDetailLoading(false);
    }
  };

  /** Where this subscription's hotel/spa lives, if the user may open it. */
  const entityLinkFor = (entry) => {
    const link = ENTITY_LINKS[entry?.entityType];
    if (!link || !entry?.entityId) return null;
    if (!hasPermission(link.permission)) return null;
    // entityId is an ObjectId, or a populated object if that ever changes
    const id = typeof entry.entityId === 'object' ? entry.entityId._id : entry.entityId;
    return id ? { ...link, to: `/sy-admin/${link.path}/view/${id}` } : null;
  };

  const closeDetails = () => {
    setSelected(null);
    setDetailError('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'status-badge status-active';
      case 'expired':
        return 'status-badge status-expired';
      case 'cancelled':
        return 'status-badge status-cancelled';
      default:
        return 'status-badge';
    }
  };

  const getDaysRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading && history.length === 0) {
    return (
      <Layout>
        <div className="subscription-history-container">
          <div className="history-header">
            <div className="history-header-content">
              <div className="skeleton skeleton-text" style={{ width: '300px', height: '40px', borderRadius: '8px', marginBottom: '8px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '400px', height: '20px', borderRadius: '4px' }}></div>
            </div>
          </div>

          {/* Filters Skeleton */}
          <div className="history-filters">
            <div className="filter-group">
              <div className="skeleton skeleton-text" style={{ width: '100px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
            </div>
            <div className="filter-group">
              <div className="skeleton skeleton-text" style={{ width: '120px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
            </div>
            <div className="filter-group">
              <div className="skeleton skeleton-text" style={{ width: '80px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
            </div>
          </div>

          {/* Stats Skeleton */}
          <div className="history-stats">
            {[1, 2, 3].map((index) => (
              <div key={index} className="stat-card">
                <div className="skeleton skeleton-text" style={{ width: '80px', height: '32px', borderRadius: '8px', margin: '0 auto 8px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '120px', height: '16px', borderRadius: '4px', margin: '0 auto' }}></div>
              </div>
            ))}
          </div>

          {/* Table Skeleton */}
          <div className="history-table-container">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Entity</th>
                  <th>Subscription</th>
                  <th>Type</th>
                  <th>From Date</th>
                  <th>To Date</th>
                  <th>Days Remaining</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
                  <tr key={index} className="skeleton-row">
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '70%', height: '16px', marginBottom: '4px' }}></div>
                      <div className="skeleton skeleton-text" style={{ width: '50%', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '80%', height: '16px', marginBottom: '4px' }}></div>
                      <div className="skeleton skeleton-text" style={{ width: '60%', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '100px', height: '24px', borderRadius: '12px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '120px', height: '16px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '120px', height: '16px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '80px', height: '16px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '100px', height: '16px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '80px', height: '24px', borderRadius: '12px' }}></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="subscription-history-container">
        <div className="history-header">
          <div className="history-header-content">
            <h1>Subscription History</h1>
            <p className="history-subtitle">
              {filters.expiring
                ? `Active subscriptions expiring within the next ${filters.expiring} days`
                : 'View all subscription activations and expirations'}
            </p>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="history-filters">
          <div className="filter-group">
            <label>Entity Type</label>
            <select
              name="entityType"
              value={filters.entityType}
              onChange={handleFilterChange}
            >
              <option value="">All Types</option>
              <option value="Hotel">Hotels</option>
              <option value="Spa">Spas</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Subscription Type</label>
            <select
              name="subscriptionType"
              value={filters.subscriptionType}
              onChange={handleFilterChange}
            >
              <option value="">All Subscriptions</option>
              <option value="verification">Verification</option>
              <option value="featured">Featured</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Expiring</label>
            <select
              name="expiring"
              value={filters.expiring}
              onChange={handleFilterChange}
            >
              <option value="">Any time</option>
              <option value="7">Within 7 days</option>
              <option value="15">Within 15 days</option>
              <option value="30">Within 30 days</option>
            </select>
          </div>

          {hasFilters && (
            <div className="filter-group filter-group-actions">
              <button type="button" className="btn btn-secondary" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="history-stats">
          <div className="stat-card">
            <h3>{pagination.total}</h3>
            <p>Total Records</p>
          </div>
          <div className="stat-card">
            <h3>{history.filter(h => h.status === 'active').length}</h3>
            <p>Active Subscriptions</p>
          </div>
          <div className="stat-card">
            <h3>{history.filter(h => h.status === 'expired').length}</h3>
            <p>Expired</p>
          </div>
        </div>

        {/* History Table */}
        {history.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <h2>No subscription history found</h2>
            <p>
              {hasFilters
                ? 'Nothing matches these filters.'
                : 'Subscription history will appear here when subscriptions are activated.'}
            </p>
            {hasFilters && (
              <button type="button" className="btn btn-secondary" onClick={clearFilters}>Clear filters</button>
            )}
          </div>
        ) : (
          <>
            <div className="history-table-container">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Entity</th>
                    <th>Subscription</th>
                    <th>Type</th>
                    <th>From Date</th>
                    <th>To Date</th>
                    <th>Days Remaining</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((entry) => {
                    const daysRemaining = getDaysRemaining(entry.expiresAt);
                    const entityLink = entityLinkFor(entry);
                    return (
                      <tr key={entry._id}>
                        <td>
                          <div>
                            {entityLink ? (
                              <Link to={entityLink.to} className="entity-link" title={`Open this ${entityLink.label}`}>
                                <strong>{entry.entityName}</strong>
                              </Link>
                            ) : (
                              <strong>{entry.entityName}</strong>
                            )}
                            <p className="table-meta">{entry.entityType}</p>
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong>{entry.subscriptionName}</strong>
                            <p className="table-meta">
                              {entry.subscriptionDuration} {entry.subscriptionDurationUnit}
                            </p>
                          </div>
                        </td>
                        <td>
                          <span className={`type-badge type-${entry.subscriptionType}`}>
                            {entry.subscriptionType}
                          </span>
                        </td>
                        <td>{formatDate(entry.activatedAt)}</td>
                        <td>{formatDate(entry.expiresAt)}</td>
                        <td>
                          {daysRemaining !== null ? (
                            <span className={daysRemaining > 0 ? 'days-remaining' : 'days-expired'}>
                              {daysRemaining > 0 ? `${daysRemaining} days` : 'Expired'}
                            </span>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td>
                          {entry.subscriptionCurrency === 'INR' ? '₹' : entry.subscriptionCurrency}{' '}
                          {entry.subscriptionPrice}
                        </td>
                        <td>
                          <span className={getStatusBadgeClass(entry.status)}>
                            {entry.status}
                          </span>
                        </td>
                        <td>
                          <div className="table-actions">
                            <button
                              type="button"
                              onClick={() => openDetails(entry)}
                              className="btn btn-edit btn-icon-only"
                              title="View subscription"
                            >
                              <span className="btn-icon">👁️</span>
                            </button>
                            {(() => {
                              const link = entityLinkFor(entry);
                              if (!link) return null;
                              return (
                                <button
                                  type="button"
                                  onClick={() => navigate(link.to)}
                                  className="btn btn-edit btn-icon-only"
                                  title={`Open this ${link.label}`}
                                >
                                  <span className="btn-icon">🏨</span>
                                </button>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              page={pagination.currentPage}
              pages={pagination.pages}
              total={pagination.total}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={(size) => { setPageSize(size); setFilters((f) => ({ ...f, page: 1 })); }}
              loading={loading}
              noun="records"
            />
          </>
        )}

        {/* ------------------------------ details ------------------------------ */}
        {selected && (
          <div className="customers-modal" onClick={closeDetails} role="presentation">
            <div
              className="customers-modal-content"
              onClick={(e) => e.stopPropagation()}
              role="presentation"
              style={{ maxWidth: 640 }}
            >
              <div className="customers-modal-header">
                <h2>Subscription details</h2>
                <button type="button" className="modal-close" onClick={closeDetails}>&times;</button>
              </div>

              <div className="customers-modal-body">
                {detailError && <div className="error-message" style={{ marginBottom: 14 }}>{detailError}</div>}

                <div className="sub-detail">
                  <div className="sub-detail-head">
                    <div>
                      {(() => {
                        const link = entityLinkFor(selected);
                        return link
                          ? <Link to={link.to} className="entity-link"><strong>{selected.entityName}</strong></Link>
                          : <strong>{selected.entityName}</strong>;
                      })()}
                      <span className="sub-detail-sub">{selected.entityType}</span>
                    </div>
                    <span className={getStatusBadgeClass(selected.status)}>{selected.status}</span>
                  </div>

                  <dl className="sub-detail-grid">
                    <div>
                      <dt>Plan</dt>
                      <dd>{selected.subscriptionName}</dd>
                    </div>
                    <div>
                      <dt>Plan type</dt>
                      <dd style={{ textTransform: 'capitalize' }}>{selected.subscriptionType}</dd>
                    </div>
                    <div>
                      <dt>Price paid</dt>
                      <dd>
                        {selected.subscriptionCurrency === 'INR' ? '₹' : selected.subscriptionCurrency}{' '}
                        {selected.subscriptionPrice}
                      </dd>
                    </div>
                    <div>
                      <dt>Duration</dt>
                      <dd>{selected.subscriptionDuration} {selected.subscriptionDurationUnit}</dd>
                    </div>
                    <div>
                      <dt>Activated on</dt>
                      <dd>{formatDate(selected.activatedAt)}</dd>
                    </div>
                    <div>
                      <dt>Expires on</dt>
                      <dd>{formatDate(selected.expiresAt)}</dd>
                    </div>
                    <div>
                      <dt>Time left</dt>
                      <dd>
                        {(() => {
                          const days = getDaysRemaining(selected.expiresAt);
                          if (days === null) return 'N/A';
                          return days > 0 ? `${days} days` : 'Expired';
                        })()}
                      </dd>
                    </div>
                    {selected.deactivatedAt && (
                      <div>
                        <dt>Deactivated on</dt>
                        <dd>{formatDate(selected.deactivatedAt)}</dd>
                      </div>
                    )}
                    <div>
                      <dt>Activated by</dt>
                      <dd>
                        {detailLoading
                          ? 'Loading…'
                          : selected.activatedBy?.username || 'Not recorded'}
                      </dd>
                    </div>
                    <div>
                      <dt>Recorded on</dt>
                      <dd>{formatDate(selected.createdAt)}</dd>
                    </div>
                  </dl>

                  <span className="sub-detail-id">ID {selected._id}</span>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: 24, paddingTop: 0 }}>
                {(() => {
                  const link = entityLinkFor(selected);
                  if (!link) return null;
                  return (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => navigate(link.to)}
                    >
                      Open this {link.label}
                    </button>
                  );
                })()}
                <button type="button" className="btn btn-secondary" onClick={closeDetails}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SubscriptionHistory;

